import { useAtom, useAtomValue } from 'jotai';
import { useCallback } from 'react';

import {
  CONNECTION_STATUS,
  chronicleDeviceStateAtom,
  chronicleIsInputAtom,
  chronicleSessionIdAtom,
  chronicleShowSpeakerAtom,
} from '@/atoms/chronicle';
import { trackHardwareInputModeSelected } from '@/utils/analytics/track-hardware-events';

/**
 * Manages Chronicle as the active microphone input source.
 *
 * Auto-selection (connect → set true, disconnect → set false) is handled
 * globally by `ChronicleStateProvider` so the side nav gets the correct
 * input flag regardless of which page the user is on.
 *
 * This hook exposes manual select/deselect for the `TauriMicrophoneSelector`
 * dropdown and read-only derived state for UI gating.
 */
export function useChronicleInputSelector() {
  const [isChronicleInput, setIsChronicleInput] = useAtom(chronicleIsInputAtom);
  const showSpeaker = useAtomValue(chronicleShowSpeakerAtom);
  const deviceState = useAtomValue(chronicleDeviceStateAtom);
  const sessionId = useAtomValue(chronicleSessionIdAtom);

  const isRecording = sessionId !== null;

  const isChronicleConnected =
    deviceState.connectionStatus === CONNECTION_STATUS.CONNECTED;

  const selectChronicle = useCallback(() => {
    setIsChronicleInput(true);
    trackHardwareInputModeSelected({
      trigger: 'manual',
      sessionIdLocal: sessionId ?? '',
    });
  }, [setIsChronicleInput, sessionId]);

  const deselectChronicle = useCallback(() => {
    setIsChronicleInput(false);
  }, [setIsChronicleInput]);

  return {
    /** Whether Chronicle is the currently selected input. */
    isChronicleInput,
    /** Whether the speaker/output dropdown should be shown. */
    showSpeaker,
    /** Whether input switching is locked (during recording). */
    isInputLocked: isRecording,
    /** Programmatically select Chronicle as input. */
    selectChronicle,
    /** Programmatically deselect Chronicle (switch to another mic). */
    deselectChronicle,
    /** Whether a Chronicle device is currently connected. */
    isChronicleConnected,
  };
}
